import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';

const OperationsDashboard = () => {
  const [inspections, setInspections] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [inspectionForm, setInspectionForm] = useState({
    findings: '',
    notes: '',
    recommendation: 'approve',
  });
  const [checkinForm, setCheckinForm] = useState({
    inventory: [],
  });
  const [checkoutForm, setCheckoutForm] = useState({
    inventory: [],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [inspectionsRes, bookingsRes] = await Promise.all([
        api.get('/api/operations/inspections'),
        api.get('/api/bookings'),
      ]);
      const inspectionsList = inspectionsRes.data?.properties ?? inspectionsRes.data ?? [];
      const reservationsList = bookingsRes.data?.reservations ?? bookingsRes.data ?? [];
      setInspections(Array.isArray(inspectionsList) ? inspectionsList.filter(i => (i.status || '').toLowerCase() === 'scheduled') : []);
      setBookings(Array.isArray(reservationsList) ? reservationsList.filter(b => ['Confirmed', 'CheckedIn', 'confirmed', 'checked_in'].includes(b.status)) : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleCompleteInspection = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('findings', inspectionForm.findings);
    formData.append('notes', inspectionForm.notes);
    formData.append('recommendation', inspectionForm.recommendation);

    try {
      const id = selectedInspection.id ?? selectedInspection._id;
      await api.post(`/api/operations/inspections/${id}/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSelectedInspection(null);
      setInspectionForm({ findings: '', notes: '', recommendation: 'approve' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to complete inspection');
    }
  };

  const handleCheckin = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('inventory', JSON.stringify(checkinForm.inventory));

    try {
      const id = selectedBooking.id ?? selectedBooking._id;
      await api.patch(`/api/bookings/${id}/check-in`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSelectedBooking(null);
      setCheckinForm({ inventory: [] });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to check in');
    }
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('inventory', JSON.stringify(checkoutForm.inventory));

    try {
      const id = selectedBooking.id ?? selectedBooking._id;
      await api.patch(`/api/bookings/${id}/check-out`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSelectedBooking(null);
      setCheckoutForm({ inventory: [] });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to check out');
    }
  };

  return (
    <DashboardLayout title="Operations Dashboard">
      <div className="card">
        <h2>Scheduled Inspections</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Property ID</th>
              <th>Scheduled Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {inspections.map((inspection) => (
              <tr key={inspection.id}>
                <td>{inspection.propertyId}</td>
                <td>{new Date(inspection.scheduledDate).toLocaleString()}</td>
                <td>
                  <span className="badge badge-warning">{inspection.status}</span>
                </td>
                <td>
                  <button
                    onClick={() => setSelectedInspection(inspection)}
                    className="btn btn-primary btn-sm"
                  >
                    Complete Inspection
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedInspection && (
        <div className="card">
          <h3>Complete Inspection</h3>
          <form onSubmit={handleCompleteInspection}>
            <div className="form-group">
              <label>Findings</label>
              <textarea
                value={inspectionForm.findings}
                onChange={(e) => setInspectionForm({ ...inspectionForm, findings: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={inspectionForm.notes}
                onChange={(e) => setInspectionForm({ ...inspectionForm, notes: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Recommendation</label>
              <select
                value={inspectionForm.recommendation}
                onChange={(e) => setInspectionForm({ ...inspectionForm, recommendation: e.target.value })}
              >
                <option value="approve">Approve</option>
                <option value="reject">Reject</option>
              </select>
            </div>
            <button type="submit" className="btn btn-success">Submit</button>
            <button
              type="button"
              onClick={() => setSelectedInspection(null)}
              className="btn btn-secondary"
              style={{ marginLeft: '10px' }}
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      <div className="card">
        <h2>Bookings - Check In/Out</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Booking ID</th>
              <th>Property ID</th>
              <th>Status</th>
              <th>Check In Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((booking) => (
              <tr key={booking.id}>
                <td>{booking.id}</td>
                <td>{booking.propertyId}</td>
                <td>
                  <span className={`badge badge-${booking.status === 'confirmed' ? 'info' : 'success'}`}>
                    {booking.status}
                  </span>
                </td>
                <td>{new Date(booking.checkInDate).toLocaleDateString()}</td>
                <td>
                  {booking.status === 'confirmed' && (
                    <button
                      onClick={() => setSelectedBooking({ ...booking, action: 'checkin' })}
                      className="btn btn-primary btn-sm"
                    >
                      Check In
                    </button>
                  )}
                  {booking.status === 'checked_in' && (
                    <button
                      onClick={() => setSelectedBooking({ ...booking, action: 'checkout' })}
                      className="btn btn-success btn-sm"
                    >
                      Check Out
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedBooking && selectedBooking.action === 'checkin' && (
        <div className="card">
          <h3>Check In - Booking {selectedBooking.id}</h3>
          <form onSubmit={handleCheckin}>
            <div className="form-group">
              <label>Inventory (JSON format)</label>
              <textarea
                value={JSON.stringify(checkinForm.inventory, null, 2)}
                onChange={(e) => {
                  try {
                    setCheckinForm({ inventory: JSON.parse(e.target.value) });
                  } catch (err) {
                    // Invalid JSON, keep as is
                  }
                }}
                placeholder='[{"name": "TV", "quantity": 1, "condition": "good"}, ...]'
                rows="10"
              />
            </div>
            <button type="submit" className="btn btn-success">Check In</button>
            <button
              type="button"
              onClick={() => setSelectedBooking(null)}
              className="btn btn-secondary"
              style={{ marginLeft: '10px' }}
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {selectedBooking && selectedBooking.action === 'checkout' && (
        <div className="card">
          <h3>Check Out - Booking {selectedBooking.id}</h3>
          <form onSubmit={handleCheckout}>
            <div className="form-group">
              <label>Inventory (JSON format)</label>
              <textarea
                value={JSON.stringify(checkoutForm.inventory, null, 2)}
                onChange={(e) => {
                  try {
                    setCheckoutForm({ inventory: JSON.parse(e.target.value) });
                  } catch (err) {
                    // Invalid JSON, keep as is
                  }
                }}
                placeholder='[{"name": "TV", "quantity": 1, "condition": "good"}, ...]'
                rows="10"
              />
            </div>
            <button type="submit" className="btn btn-success">Check Out</button>
            <button
              type="button"
              onClick={() => setSelectedBooking(null)}
              className="btn btn-secondary"
              style={{ marginLeft: '10px' }}
            >
              Cancel
            </button>
          </form>
        </div>
      )}
    </DashboardLayout>
  );
};

export default OperationsDashboard;

